import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAssignedProductsData } from '../../utilities/api';

export const fetchAssignedProductsData = createAsyncThunk('viewAssignedProducts/fetch', async ({ rolename, clientname }, { rejectWithValue }) => {
    try {
        const data = await getAssignedProductsData({ rolename, clientname });
        return data;
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch data');
    }
});

const initialState = {
    branches: [],
    categories: [],
    companies: [],
    products: [],
    allProducts: [],
    loading: false,
    error: null,
    selectedBranch: null,
    selectedCategory: null,
    selectedCompany: null
};

const viewAssignedProductSlice = createSlice({
    name: 'viewAssignedProducts',
    initialState,
    reducers: {
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
            state.selectedCategory = null;
            state.selectedCompany = null;
        },
        setSelectedCategory: (state, action) => {
            state.selectedCategory = action.payload;
            state.selectedCompany = null;
        },
        setSelectedCompany: (state, action) => {
            state.selectedCompany = action.payload;
        },
        resetFilters: (state) => {
            state.selectedBranch = null;
            state.selectedCategory = null;
            state.selectedCompany = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAssignedProductsData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAssignedProductsData.fulfilled, (state, action) => {
                state.loading = false;
                const raw = action.payload || {};
                state.branches = Array.isArray(raw.branchs) ? raw.branchs : [];
                state.categories = Array.isArray(raw.categorys) ? raw.categorys : [];
                state.companies = Array.isArray(raw.companies) ? raw.companies : [];
                state.products = Array.isArray(raw.splproducts) ? raw.splproducts : [];
                state.allProducts = Array.isArray(raw.allproducts) ? raw.allproducts : [];
            })
            .addCase(fetchAssignedProductsData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { setSelectedBranch, setSelectedCategory, setSelectedCompany, resetFilters } = viewAssignedProductSlice.actions;

export const selectBranches = (state) => state.viewAssignedProducts.branches;
export const selectAllProducts = (state) => state.viewAssignedProducts.allProducts;
export const selectProducts = (state) => state.viewAssignedProducts.products;
export const selectAllCompanies = (state) => state.viewAssignedProducts.companies;
export const selectLoading = (state) => state.viewAssignedProducts.loading;
export const selectError = (state) => state.viewAssignedProducts.error;
export const selectSelectedBranch = (state) => state.viewAssignedProducts.selectedBranch;
export const selectSelectedCategory = (state) => state.viewAssignedProducts.selectedCategory;
export const selectSelectedCompany = (state) => state.viewAssignedProducts.selectedCompany;

export const selectAllCategories = (state) => state.viewAssignedProducts.categories;

export const selectFilteredCategories = (state) => {
    const { categories, selectedBranch } = state.viewAssignedProducts;
    if (!selectedBranch) return [];
    return categories.filter((c) => String(c.branch_id) === String(selectedBranch.branch_id));
};

export const selectFilteredCompanies = (state) => {
    const { products, selectedBranch, selectedCategory } = state.viewAssignedProducts;
    if (!selectedBranch || !selectedCategory) return [];
    const filtered = products.filter(
        (p) => String(p.branch_id) === String(selectedBranch.branch_id) && String(p.prcat_cat_id) === String(selectedCategory.code)
    );
    const uniqueCompanies = [];
    const companyIds = new Set();
    filtered.forEach((p) => {
        if (!companyIds.has(p.company_id)) {
            companyIds.add(p.company_id);
            uniqueCompanies.push({ company_id: p.company_id, company_name: p.company_name });
        }
    });
    return uniqueCompanies;
};

export const selectFilteredProducts = (state) => {
    const { products, selectedBranch, selectedCategory, selectedCompany } = state.viewAssignedProducts;
    let result = [...products];
    if (selectedBranch) result = result.filter((p) => String(p.branch_id) === String(selectedBranch.branch_id));
    if (selectedCategory) result = result.filter((p) => String(p.prcat_cat_id) === String(selectedCategory.code));
    if (selectedCompany) result = result.filter((p) => String(p.company_id) === String(selectedCompany.company_id));
    return result;
};

export default viewAssignedProductSlice.reducer;
